import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Dutch } from "flatpickr/dist/l10n/nl.js"
import { German } from "flatpickr/dist/l10n/de.js"

export default class extends Controller {
  static targets = ["input"]
  static values = {
    bookingId: { type: String, default: '' },
    currentTimeSlotId: { type: String, default: '' },
    locale: { type: String, default: 'en' },
    bookingHomepage: { type: Boolean, default: false }
  }

  connect() {
    if (this.hasInputTarget) {
      this.fetchAvailableDates();
    }
  }

  fetchAvailableDates() {
    const bookingQuery = this.bookingIdValue ? `?booking_id=${encodeURIComponent(this.bookingIdValue)}` : ''

    fetch(`/bookings/fetch_available_dates${bookingQuery}`, {
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.json()
    })
    .then(data => {
      if (data.available_dates && data.available_dates.length > 0) {
        this.availableDates = data.available_dates;
        this.initializeFlatpickr(data.available_dates)
      }
    })
    .catch(error => {
      // Keep error handling but remove console.error
    })
  }

  initializeFlatpickr(availableDates) {
    const dates = availableDates.map(date => new Date(date))
    
    const config = {
      enable: dates,
      dateFormat: "Y-m-d",
      minDate: "today",
      inline: false,
      allowInput: true,
      locale: this.localeValue === 'nl' ? Dutch : (this.localeValue === 'de' ? German : null),
      static: !this.bookingHomepageValue, // inline form will set bookingHomepageValue to true to disable static
      appendTo: this.inputTarget.parentElement, // Append to the input's parent
      onChange: (selectedDates) => {
        if (selectedDates.length > 0) {
          const selected = selectedDates[0]
          const formattedDate = this.formatDate(selected)
          
          this.dispatch("dateSelected", { 
            detail: { 
              date: formattedDate,
              bookingId: this.bookingIdValue || '',
              currentTimeSlotId: this.currentTimeSlotIdValue || ''
            } 
          })
        }
      },
      // Removed onOpen/onClose hooks to avoid premature blur validation
    }
    
    this.flatpickrInstance = flatpickr(this.inputTarget, config)
    // Removed blur listener: validation will occur only on explicit input submits
  }

  validateManualInput() {
    const inputValue = this.inputTarget.value;
    
    if (!inputValue) return;
    
    let inputDate;
    try {
      // Parse YYYY-MM-DD format
      const parts = inputValue.split('-');
      if (parts.length === 3) {
        inputDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        // Try native date parsing as fallback
        inputDate = new Date(inputValue);
      }
    } catch (e) {
      this.resetToValidDate();
      return;
    }
    
    // Check if the date is valid
    if (isNaN(inputDate.getTime())) {
      this.resetToValidDate();
      return;
    }
    
    // Format the date for comparison
    const formattedInputDate = this.formatDate(inputDate);
    
    // Check if the date is in the available dates
    if (!this.availableDates || !this.availableDates.includes(formattedInputDate)) {
      this.resetToValidDate();
      return;
    }
    
    // Valid date, update the flatpickr instance
    this.flatpickrInstance.setDate(formattedInputDate);
  }
  
  resetToValidDate() {
    // Reset to the original value or today's date
    if (this.originalValue) {
      this.inputTarget.value = this.originalValue;
    } else {
      this.flatpickrInstance.clear();
    }
  }
  
  formatDate(dateObj) {
    return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
  }
}